import { ref, computed } from 'vue'
import { useLocale } from '@/composables/useLocale'
import { fetchDocArticle } from '@/api/docs'
import type { DocArticle } from '@/types/docs'

export function useDocArticle() {
  const { currentLocale } = useLocale()

  const article = ref<DocArticle | null>(null)
  const loading = ref(false)
  const error = ref<string | null>(null)
  const notFound = ref(false)

  // True when the API fell back to 'en' because the requested locale was not available
  const localeFallback = computed<boolean>(() => {
    if (!article.value) return false
    return (
      article.value.requested_locale !== undefined &&
      article.value.requested_locale !== article.value.locale
    )
  })

  async function fetch(category: string, slug: string): Promise<void> {
    loading.value = true
    error.value = null
    notFound.value = false
    article.value = null
    try {
      article.value = await fetchDocArticle(category, slug, currentLocale.value)
    } catch (e: unknown) {
      if (isNotFoundError(e)) {
        notFound.value = true
      } else {
        error.value = 'docs.article.error'
      }
    } finally {
      loading.value = false
    }
  }

  return { article, loading, error, notFound, localeFallback, fetch }
}

function isNotFoundError(e: unknown): boolean {
  return (
    typeof e === 'object' &&
    e !== null &&
    'response' in e &&
    typeof (e as { response: { status: number } }).response === 'object' &&
    (e as { response: { status: number } }).response.status === 404
  )
}
